import axios from 'Utils/axios'
import { useEffect, useState, useRef } from 'react'

export interface IUseFetchDimensions {
  dimension_code?: string
}
export default function useFetchDimensions({ dimension_code }: IUseFetchDimensions) {
  const [options, setOptions] = useState<any[]>([])
  const [keyword, setKeyword] = useState('')
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(1)
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null)
  const sentryRef = useRef(null)
  
  // Helper function to split keywords into batches of 10
  const splitKeywords = (keywordString: string): string[][] => {
    if (!keywordString.trim()) return []
    
    // Split by comma, space, or newline and filter empty strings
    const keywords = keywordString
      .split(/[\s,\n]+/)
      .map(k => k.trim())
      .filter(k => k.length > 0)
    
    // Split into batches of 10
    const batches: string[][] = []
    for (let i = 0; i < keywords.length; i += 10) {
      batches.push(keywords.slice(i, i + 10))
    }
    
    return batches
  }
  
  const fetchDimensions = async (currentPage = 1, isLoadMore = false) => {
    try {
      setLoading(true)
      
      const keywordBatches = splitKeywords(keyword)
      
      // If no keywords or single batch, use normal search
      if (keywordBatches.length <= 1) {
        const resp = await axios.get('/audience-dimensions/dimensions', {
          params: {
            dimension_code,
            keyword: keyword || '',
            per_page: 50,
            page: currentPage,
          },
        })
        
        const newData = resp.data.data.map((op) => ({ label: op?.name, value: op?.code, ...op }))
        
        if (isLoadMore) {
          setOptions(prev => [...prev, ...newData])
        } else {
          setOptions(newData)
          setPage(1)
        }
        
        setHasMore(newData.length === 50)
        
      } else {
        // Multiple batches - search in parallel
        setBatchProgress({ current: 0, total: keywordBatches.length })
        
        const batchPromises = keywordBatches.map((batch, index) => 
          axios.get('/audience-dimensions/dimensions', {
            params: {
              dimension_code,
              keyword: batch.join(' '), // Join keywords with space
              per_page: 50, // Each batch can return up to 50 results
              page: 1, // Always search from page 1 for each batch
            },
          }).then(response => {
            setBatchProgress(prev => prev ? { ...prev, current: prev.current + 1 } : null)
            return response
          })
        )
        
        const responses = await Promise.all(batchPromises)
        
        // Clear batch progress
        setBatchProgress(null)
        
        // Merge results from all batches
        const allData = responses.reduce((acc: any[], resp) => {
          const batchData = resp.data.data.map((op: any) => ({ label: op?.name, value: op?.code, ...op }))
          return [...acc, ...batchData]
        }, [])
        
        // Remove duplicates based on value/code
        const uniqueData = allData.filter((item: any, index: number, arr: any[]) => 
          arr.findIndex((t: any) => t.value === item.value) === index
        )
        
        if (isLoadMore) {
          setOptions(prev => [...prev, ...uniqueData])
        } else {
          setOptions(uniqueData)
          setPage(1)
        }
        
        // For multiple keywords search, disable pagination
        setHasMore(false)
      }
      
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
      setBatchProgress(null)
    }
  }

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchDimensions(nextPage, true)
    }
  }

  // Reset khi keyword thay đổi
  useEffect(() => {
    if (dimension_code) {
      setPage(1)
      setHasMore(true)
      setBatchProgress(null)
      fetchDimensions(1, false)
    }
  }, [dimension_code, keyword])

  return {
    options,
    setKeyword,
    loading,
    hasMore,
    onLoadMore: handleLoadMore,
    sentryRef,
    batchProgress,
  }
}
